import { Link } from 'react-router-dom';
import Logo from '@/components/Logo';
import { useSiteContent } from '@/lib/useSiteContent';
import {
  MessageCircle, Kanban, FileText, Calculator, Sparkles, Smartphone, ArrowRight, Check,
} from 'lucide-react';

const FALLBACK = {
  hero_pill: 'Cotiza, da seguimiento y cierra — sin perderte en WhatsApp',
  hero_title: 'La memoria operativa',
  hero_highlight: 'de tu tour operador.',
  hero_subtitle: 'Routiq convierte tus chats de WhatsApp en cotizaciones estructuradas, con pipeline visual, PDF profesional y un motor de precios 100% configurable.',
  cta_primary: 'Empezar ahora',
  cta_secondary: 'Ver características',
  waitlist_text: '+30 tour operadores ya en lista de espera',
  hero_image_url: '',
  feature_image_url: '',
  features_title: 'Todo lo que tu equipo necesita antes de la venta.',
  features_subtitle: 'Fareharbor y Bokun resuelven la post-venta. Routiq cubre el hueco: desde el primer mensaje hasta el cierre.',
  final_cta_title: 'Digitaliza tu cotización hoy.',
  final_cta_subtitle: 'Reserva una demo de 20 min o entra directo con tus credenciales de prueba.',
  sections: [
    { key: 'features', label: 'Características', visible: true },
    { key: 'how', label: 'Cómo funciona', visible: true },
    { key: 'pricing', label: 'Planes / Precios', visible: true },
    { key: 'affiliates', label: 'Logos de empresas afiliadas', visible: true },
    { key: 'final_cta', label: 'Llamado final (CTA)', visible: true },
  ],
  affiliates_title: 'Empresas que ya operan con Routiq',
  affiliate_logos: [],
  pricing_pill: 'Planes',
  pricing_title: 'Precios simples que crecen con tu operación.',
  pricing_subtitle: 'MXN al mes por empresa. Sin costo por mensaje. Sin costo por usuario extra hasta el límite del plan.',
  pricing_tiers: [
    { name: 'Starter', price: '$890', period: '/mes', highlight: false, cta: 'Comenzar', perks: ['1 número WhatsApp', 'Hasta 3 ejecutivos', 'Cotizaciones ilimitadas', 'PDF con branding'] },
    { name: 'Pro', price: '$1,890', period: '/mes', highlight: true, cta: 'Comenzar', perks: ['Hasta 5 números', 'Hasta 15 ejecutivos', 'IA operativa', 'Kanban + alertas', 'Motor de precios avanzado'] },
    { name: 'Enterprise', price: 'A medida', period: '/mes', highlight: false, cta: 'Comenzar', perks: ['Números ilimitados', 'Meta API oficial', 'SLA dedicado', 'Onboarding + capacitación'] },
  ],
};

// Nav links tied to a section key so they hide when the section is hidden.
const NAV_LINKS = [
  { key: 'features', href: '#features', label: 'Producto' },
  { key: 'how', href: '#how', label: 'Cómo funciona' },
  { key: 'pricing', href: '#pricing', label: 'Planes' },
];

function NavBar({ sections }) {
  const visible = (key) => (sections || []).some((s) => s.key === key && s.visible);
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-ink-100/60">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <Logo size={28} />
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-ink-700">
          {NAV_LINKS.filter((l) => visible(l.key)).map((l) => (
            <a key={l.key} href={l.href} className="hover:text-brand-500 transition-colors">{l.label}</a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="btn-ghost text-sm" data-testid="nav-login-link">Entrar</Link>
          <a href="#demo" className="btn-primary text-sm hidden sm:inline-flex" data-testid="nav-cta-demo">
            Pedir demo <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </header>
  );
}

function Hero({ c }) {
  return (
    <section className="relative overflow-hidden">
      {c.hero_image_url && <img src={c.hero_image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" data-testid="landing-hero-image" />}
      <div className="absolute inset-0 bg-gradient-to-br from-cream/95 via-cream/80 to-brand-50/90" />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7 space-y-6 animate-fade-up">
          <span className="pill bg-brand-50 text-brand-500" data-testid="hero-pill">
            <Sparkles className="w-3.5 h-3.5" /> {c.hero_pill}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] font-bold text-ink-900 tracking-tight" data-testid="landing-hero-title">
            {c.hero_title} <br />
            <span className="text-brand-500">{c.hero_highlight}</span>
          </h1>
          <p className="text-lg text-ink-500 max-w-xl leading-relaxed" data-testid="landing-hero-subtitle">{c.hero_subtitle}</p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Link to="/login" className="btn-primary" data-testid="hero-cta-primary">
              {c.cta_primary} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#features" className="btn-secondary" data-testid="hero-cta-secondary">{c.cta_secondary}</a>
          </div>
          <div className="flex items-center gap-4 pt-4 text-sm text-ink-500">
            <div className="flex -space-x-2">
              {['#185FA5', '#378ADD', '#E1F5EE', '#FAEEDA'].map((col, i) => (
                <div key={i} className="w-8 h-8 rounded-full border-2 border-white" style={{ background: col }} />
              ))}
            </div>
            <span><b className="text-ink-900">{c.waitlist_text}</b></span>
          </div>
        </div>
        <div className="md:col-span-5"><MockDashboardCard /></div>
      </div>
    </section>
  );
}

function MockDashboardCard() {
  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-gradient-to-br from-brand-400/30 to-mint-200/50 rounded-[28px] blur-2xl" />
      <div className="relative card-surface p-5 md:p-6 space-y-4 animate-fade-up" style={{ animationDelay: '0.15s' }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase font-bold tracking-widest text-ink-400">Pipeline</p>
            <p className="font-display text-xl font-semibold">Cotizaciones activas</p>
          </div>
          <span className="pill bg-mint-100 text-emerald-700">+24% vs. mes pasado</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Nuevas', n: 12, tone: 'bg-ink-100 text-ink-700' },
            { label: 'Cotizando', n: 8, tone: 'bg-brand-50 text-brand-500' },
            { label: 'Enviadas', n: 5, tone: 'bg-peach-100 text-amber-700' },
          ].map((card) => (
            <div key={card.label} className="rounded-xl border border-ink-100 p-3">
              <p className="text-[11px] uppercase font-bold tracking-wider text-ink-400">{card.label}</p>
              <p className="font-display font-bold text-2xl text-ink-900">{card.n}</p>
              <span className={`pill mt-2 ${card.tone}`}>activas</span>
            </div>
          ))}
        </div>
        <div className="rounded-xl bg-gradient-to-r from-brand-500 to-accent p-4 text-white">
          <p className="text-[11px] uppercase font-bold tracking-widest opacity-80">Proyección del mes</p>
          <p className="font-display text-3xl font-bold">$428,500 MXN</p>
          <p className="text-sm opacity-90">Basado en cotizaciones en negociación</p>
        </div>
      </div>
    </div>
  );
}

function Features({ c }) {
  const items = [
    { icon: MessageCircle, title: 'WhatsApp multi-número', text: 'Conecta todos tus números por QR. Un inbox, sub-hilos por cotización, sin chats mezclados.' },
    { icon: Kanban, title: 'Pipeline Kanban visual', text: 'De nueva consulta a cerrada en un tablero que entiende tu equipo. Con alertas de inactividad.' },
    { icon: Calculator, title: 'Motor de precios propio', text: 'Tus fórmulas, tus márgenes, tus comisiones por canal. Nada hardcodeado. Configurable en segundos.' },
    { icon: FileText, title: 'PDF profesional', text: 'Cotización con tu branding, itinerario y desglose. Lista para enviar desde el mismo chat.' },
    { icon: Sparkles, title: 'IA operativa', text: 'Resúmenes automáticos del chat, detección de oportunidades y campos faltantes.' },
    { icon: Smartphone, title: 'PWA instalable', text: 'Úsala como app nativa en Android e iOS. Offline en lo esencial. Sin pasar por stores.' },
  ];
  return (
    <section id="features" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-2xl">
          <p className="pill bg-mint-100 text-emerald-800" data-testid="features-pill">Producto</p>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-ink-900 mt-4 tracking-tight" data-testid="landing-features-title">{c.features_title}</h2>
          <p className="text-ink-500 mt-4 text-lg">{c.features_subtitle}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {items.map(({ icon: Icon, title, text }, i) => (
            <div key={title} className="card-surface p-6 animate-fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
              <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-500 flex items-center justify-center mb-4"><Icon className="w-5 h-5" /></div>
              <h3 className="font-display font-semibold text-lg text-ink-900">{title}</h3>
              <p className="text-ink-500 text-sm mt-2 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks({ c }) {
  const steps = [
    { n: '01', title: 'Conecta WhatsApp', text: 'Escanea el QR una vez y deja que Routiq reciba todos tus chats.' },
    { n: '02', title: 'Arma tu catálogo', text: 'Carga paquetes, hoteles, ocupaciones y tus fórmulas de precios.' },
    { n: '03', title: 'Cotiza en 2 minutos', text: 'Constructor guiado. PDF listo con el branding de tu empresa.' },
    { n: '04', title: 'Da seguimiento', text: 'Kanban con alertas si una cotización lleva demasiado sin movimiento.' },
  ];
  return (
    <section id="how" className="py-20 md:py-28 bg-white border-y border-ink-100">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-5">
          <p className="pill bg-brand-50 text-brand-500">Cómo funciona</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-ink-900 mt-4 tracking-tight">Del caos de WhatsApp a un flujo que tu equipo sigue.</h2>
          <p className="text-ink-500 mt-4 leading-relaxed">Arranca en un día. Sin romper lo que ya funciona en Fareharbor ni Bokun. Compatible con Meta API oficial cuando tu empresa esté lista.</p>
          {c.feature_image_url && <img src={c.feature_image_url} alt="Equipo de ventas" className="rounded-2xl mt-8 hidden md:block shadow-lg" data-testid="landing-feature-image" />}
        </div>
        <ol className="md:col-span-7 space-y-4">
          {steps.map(({ n, title, text }, i) => (
            <li key={n} className="flex gap-5 p-6 rounded-2xl border border-ink-100 bg-cream hover:border-brand-200 hover:bg-white transition-all animate-fade-up" style={{ animationDelay: `${i * 0.06}s` }}>
              <span className="font-display text-3xl font-bold text-brand-500 w-14 shrink-0">{n}</span>
              <div>
                <h3 className="font-display font-semibold text-lg text-ink-900">{title}</h3>
                <p className="text-ink-500 mt-1 leading-relaxed">{text}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function planIdFromTier(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('starter') || n.includes('básico') || n.includes('basico')) return 'starter';
  if (n.includes('enterprise') || n.includes('medida') || n.includes('empresa')) return 'enterprise';
  return 'pro';
}

function Pricing({ c }) {
  const tiers = (Array.isArray(c.pricing_tiers) && c.pricing_tiers.length) ? c.pricing_tiers : FALLBACK.pricing_tiers;
  return (
    <section id="pricing" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="pill bg-peach-100 text-amber-800">{c.pricing_pill}</p>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-ink-900 mt-4 tracking-tight" data-testid="landing-pricing-title">{c.pricing_title}</h2>
          <p className="text-ink-500 mt-4">{c.pricing_subtitle}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {tiers.map((t, ti) => (
            <div key={t.name + ti}
              className={`rounded-2xl p-7 transition-all ${t.highlight ? 'bg-brand-500 text-white shadow-xl scale-[1.02]' : 'bg-white border border-ink-100'}`}
              data-testid={`pricing-tier-${ti}`}>
              <p className={`text-xs uppercase font-bold tracking-widest ${t.highlight ? 'text-brand-50' : 'text-ink-400'}`}>{t.name}</p>
              <p className={`font-display text-4xl font-bold mt-2 ${t.highlight ? 'text-white' : 'text-ink-900'}`}>{t.price}<span className="text-base font-normal opacity-70"> {t.period || '/mes'}</span></p>
              <ul className={`mt-5 space-y-2 text-sm ${t.highlight ? 'text-white/90' : 'text-ink-700'}`}>
                {(t.perks || []).filter((p) => p && p.trim() !== '').map((p, pi) => (
                  <li key={p + pi} className="flex items-start gap-2"><Check className={`w-4 h-4 mt-0.5 ${t.highlight ? 'text-mint-100' : 'text-brand-500'}`} />{p}</li>
                ))}
              </ul>
              <Link to={`/signup?plan=${planIdFromTier(t.name)}`} className={`mt-6 w-full justify-center ${t.highlight ? 'btn-secondary' : 'btn-primary'}`} data-testid={`pricing-cta-${ti}`}>{t.cta || 'Comenzar'}</Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ c }) {
  return (
    <section id="demo" className="relative py-24 md:py-32 bg-brand-500 text-white overflow-hidden">
      <div className="absolute inset-0 grain opacity-20" />
      <div className="relative max-w-5xl mx-auto px-4 md:px-8 text-center space-y-6">
        <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight" data-testid="landing-finalcta-title">{c.final_cta_title}</h2>
        <p className="text-lg text-brand-50 max-w-2xl mx-auto">{c.final_cta_subtitle}</p>
        <div className="flex flex-wrap justify-center gap-3 pt-2">
          <Link to="/login" className="btn-secondary" data-testid="final-cta-login">Entrar al sistema <ArrowRight className="w-4 h-4" /></Link>
          <a href="mailto:hola@routiq.mx" className="btn-ghost !text-white hover:!bg-white/10" data-testid="final-cta-email">Escribir a ventas</a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-ink-900 text-white py-10" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <Logo size={26} white />
        <p className="text-sm text-white/60">© 2026 Routiq. Hecho para tour operadores. GDL 🇲🇽</p>
      </div>
    </footer>
  );
}

function Affiliates({ c }) {
  const logos = Array.isArray(c.affiliate_logos) ? c.affiliate_logos.filter((l) => l && l.url) : [];
  if (logos.length === 0) return null;
  const loop = [...logos, ...logos];
  return (
    <section id="affiliates" className="py-16 md:py-20 border-y border-ink-100/60 bg-white/50" data-testid="affiliates-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <p className="text-center text-sm uppercase tracking-widest font-bold text-ink-400 mb-8">{c.affiliates_title}</p>
        <div className="relative overflow-hidden marquee-pause" style={{ maskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)' }}>
          <div className="flex items-center gap-14 w-max animate-marquee">
            {loop.map((l, i) => (
              <img key={i} src={l.url} alt={l.name || 'logo'} title={l.name || ''}
                className="h-10 md:h-12 w-auto object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const SECTION_COMPONENTS = {
  features: Features,
  how: HowItWorks,
  pricing: Pricing,
  affiliates: Affiliates,
  final_cta: FinalCta,
};

export default function Landing() {
  const content = useSiteContent();
  const c = { ...FALLBACK, ...(content?.landing || {}) };
  const sections = (Array.isArray(c.sections) && c.sections.length) ? c.sections : FALLBACK.sections;
  return (
    <div className="min-h-screen bg-cream" data-testid="landing-page">
      <NavBar sections={sections} />
      <Hero c={c} />
      {sections.filter((s) => s.visible).map((s) => {
        const Comp = SECTION_COMPONENTS[s.key];
        return Comp ? <Comp key={s.key} c={c} /> : null;
      })}
      <Footer />
    </div>
  );
}
